/**
 * Fixed-timestep requestAnimationFrame loop shared by the arcade games (C).
 * onTick(tickMs) runs 0..maxSteps times per frame depending on elapsed time,
 * then onFrame() once. The owner calls stop() when its game leaves the
 * playing state (from onFrame or onTick). raf/caf are injectable for tests.
 */
export const DEFAULT_TICK_MS = 1000 / 60;

export function createGameLoop({
    onTick,
    onFrame = () => {},
    tickMs = DEFAULT_TICK_MS,
    maxSteps = 5,
    maxDelta = 250,
    raf = cb => window.requestAnimationFrame(cb),
    caf = id => window.cancelAnimationFrame(id),
}) {
    let running = false;
    let handle = null;
    let lastTs = null;
    let acc = 0;

    function frame(ts) {
        handle = null;
        if (!running) {
            return;
        }
        if (lastTs === null) {
            lastTs = ts;
        }
        acc += Math.min(maxDelta, ts - lastTs);
        lastTs = ts;
        let steps = 0;
        while (running && acc >= tickMs && steps < maxSteps) {
            onTick(tickMs);
            acc -= tickMs;
            steps += 1;
        }
        if (steps === maxSteps) {
            acc = 0;
        }
        if (!running) {
            return;
        }
        onFrame();
        if (running) {
            handle = raf(frame);
        }
    }

    return {
        start() {
            if (running) {
                return;
            }
            running = true;
            lastTs = null;
            acc = 0;
            handle = raf(frame);
        },
        stop() {
            running = false;
            if (handle !== null) {
                caf(handle);
                handle = null;
            }
        },
        isRunning() {
            return running;
        },
    };
}
